import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listChats } from '../api/client';
import { TierBadge } from '../components/TierBadge';
import { StatusBadge } from '../components/StatusBadge';
import { BotRoleBadge } from '../components/BotRoleBadge';
import { formatChatId, formatPosts, timeAgo } from '../utils/format';

const TIER_OPTIONS = ['', 'FREE', 'PRO_CREDITS', 'PRO_SUB'] as const;
const STATUS_OPTIONS = ['', 'active', 'silent', 'disabled', 'kicked'] as const;

export function ChatsPage() {
  const [tier, setTier] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['chats', tier, status, committedSearch],
    queryFn: () => listChats({ tier: tier || undefined, status: status || undefined, search: committedSearch || undefined })
  });

  return (
    <div data-testid="chats-page">
      <h1 className="text-xl font-semibold text-gray-900">Chats</h1>
      <p className="mt-1 text-sm text-gray-500">
        Telegram groups, supergroups and DMs that have the bot installed.
      </p>

      {/* Filter row */}
      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="chats-filters">
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
          data-testid="filter-tier"
        >
          {TIER_OPTIONS.map((v) => (
            <option key={v || 'any'} value={v}>
              {v === '' ? 'Any tier' : v.replace('_', ' · ')}
            </option>
          ))}
        </select>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
          data-testid="filter-status"
        >
          {STATUS_OPTIONS.map((v) => (
            <option key={v || 'any'} value={v}>
              {v === '' ? 'Any status' : v}
            </option>
          ))}
        </select>

        <div className="flex flex-1 items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setCommittedSearch(search);
            }}
            placeholder="Search project name or chatId…"
            className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm"
            data-testid="filter-search"
          />
          <button
            onClick={() => setCommittedSearch(search)}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
            data-testid="filter-search-submit"
          >
            Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-lg border border-gray-200 bg-white" data-testid="chats-table-container">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
          </div>
        )}

        {error && (
          <div className="p-4 text-sm text-red-700">
            Failed to load chats: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {data && data.chats.length === 0 && !isLoading && (
          <div className="p-12 text-center text-sm text-gray-400" data-testid="chats-empty">
            No chats match those filters.
          </div>
        )}

        {data && data.chats.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" data-testid="chats-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Chat
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500" title="Posts pulled from the X API this billing period.">
                    Posts MTD
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500" title="FREE = remaining of 100/month free quota. PRO·credits = stored balance. PRO·sub = unlimited (—).">
                    Credits left
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500" title="Bot's role in the Telegram chat (admin / member / restricted / kicked / etc).">
                    Bot role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Schedule
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500" title="Most recent scheduled-fetch event. The bot ran, regardless of outcome.">
                    Last fired
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500" title="Most recent successful in-chat report.">
                    Last posted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {data.chats.map((c) => (
                  <tr
                    key={c.chatId}
                    className="cursor-pointer hover:bg-gray-50"
                    data-testid={`chat-row-${c.chatId}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link
                        to={`/chats/${encodeURIComponent(c.chatId)}`}
                        className="text-primary-700 hover:underline"
                      >
                        {formatChatId(c.chatId)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {c.projectName || <span className="text-gray-400 italic">unset</span>}
                    </td>
                    <td className="px-4 py-3">
                      <TierBadge tier={c.tier} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} silentReason={c.silentReason} />
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono">
                      {formatPosts(c.postsMtd)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-mono">
                      {c.remainingCredits != null ? formatPosts(c.remainingCredits) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <BotRoleBadge
                        membership={c.botMembership}
                        canPost={c.botCanPost}
                        checkedAt={c.botMembershipCheckedAt}
                      />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {c.scheduleHuman || <span className="text-gray-300">none</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-gray-500">
                      {timeAgo(c.lastFiredAt)}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-gray-500">
                      {timeAgo(c.lastPostedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
